import React, { createContext, useContext, useState, useRef, ReactNode } from 'react';
import { RequestsService } from '../api/services/RequestsService';
import { BookingFormService } from '../api/services/BookingFormService';
import { RequestSummaryService } from '../services/requestSummaryService';
import { AISummaryService } from '../services/aiSummaryService';
import { useTenantData } from './TenantDataContext';
import { resolveForbiddenMessage } from '../utils/forbiddenErrorMessage';
import { Routemize_Constants_RequestStatus, Routemize_Constants_DateFilterType } from '../api';
import { Routemize_Constants_SlotRejectionReason } from '../api/models/Routemize_Constants_SlotRejectionReason';
import { Routemize_Constants_RequestOrigin } from '../api/models/Routemize_Constants_RequestOrigin';
import type { 
  Routemize_DTOs_RequestsDTO,
  Routemize_DTOs_UpdateRequestsDTO,
  Routemize_DTOs_SearchParameters_PagedSortedAndFilteredRequestDto_1,
  Routemize_DTOs_RequestAnswerDTO,
  Routemize_DTOs_AttachmentsDTO,
  Routemize_DTOs_RequestSearchResponse,
  Routemize_DTOs_Metadata_TagDTO
} from '../api';
import {
  contactAddressToFields,
  formatRequestAddressDisplay,
  mergeRequestAddressFields,
  resolveContactAddressFromDto,
  resolveRequestAddressFields,
} from '../utils/requestAddressUtils';

// Transform generated DTO to a simplified request interface for component use
export interface RequestData {
  id: string;
  date: Date;
  lastModificationTime?: Date; // Date when the request was last modified/updated
  status: 'Incomplete' | 'Active' | 'Archived' | 'History' | 'Declined';
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  contactId?: string; // ID of the associated contact
  contactAddressId?: string; // ID of the contact's address
  businessName?: string; // Business name of the contact
  appointmentType: string; // Deprecated: Combined form name and type. Use formName and formType instead
  formName?: string; // Name of the booking form (e.g., "Estimate", "Test")
  formType?: string; // Type of the booking form (e.g., "Routemize", "Request")
  bookingFormId?: string; // ID of the booking form associated with this request
  customerAddress: string;
  // Individual address components for easier access
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  leadSource?: string; // Lead source from booking form (e.g., "Website", "Referral", etc.)
  answers: Array<{ question: string; answer: string; questionId?: string; isRequestOnlyTrigger?: boolean; addedMinutes?: number; isOptionAnswer?: boolean; }>;
  attachments: Array<{ id: string; fileName: string; fileUrl: string; }>;
  assignedUserId?: string;
  notes?: string;
  projectDetail?: string;
  preferredDate?: string;
  preferredSecondaryDate?: string; // Customer's secondary date preference
  preferredTimeRange?: string; // Customer's time preference (morning, afternoon, evening, anytime)
  preferredStartTime?: string; // Start time of preferred range
  preferredEndTime?: string; // End time of preferred range
  autoRoutemized?: { date: string; time: string; };
  customerSawAvailableSlots?: boolean; // Flag indicating customer was presented with time slots
  summary: string; // AI-generated summary of the request
  declinedDate?: Date; // Date when request was declined
  declinedBy?: string; // User who declined the request
  archivedDate?: Date; // Date when request was archived
  archivedBy?: string; // User who archived the request
  lastAction?: {
    updatedDateTime?: string;
    updatedBy?: string;
    updatedAction?: string;
  };
  isOutsideServiceArea?: boolean; // Flag indicating address is outside configured service area
  isAsap?: boolean; // Flag indicating customer wants the soonest available appointment
  isFlexibleDate?: boolean; // Customer indicated they are flexible on date
  scheduleMethod?: string; // How the request was scheduled: 'Routemized', 'Manual', etc.
  origin?: string; // Request submission channel: 'Web' or 'Chatbot'
  isLiveBooking?: boolean; // True when customer is actively progressing through the booking form (granular statuses 10-15)
  tags?: Routemize_DTOs_Metadata_TagDTO[]; // Card labels assigned to this request (inline from the request record)
  noteCount?: number; // Customer + pre-booking notes, from the search's one batched note query (NC-08)
  latestInternalNotePreview?: string; // Newest pre-booking note, cut by the server (NC-08)
}

/** Options for {@link RequestsContextType.updateRequestStatus}. The reason fields are sent in `UpdateRequestsDTO`. */
export interface UpdateRequestStatusOptions {
  suppressNotifications?: boolean;
  statusReason?: string;
  statusReasonDetails?: string;
}

interface RequestsContextType {
  requests: RequestData[];
  isLoading: boolean;
  error: string | null;
  searchRequests: (params: SearchParams) => Promise<void>;
  getRequestById: (id: string) => Promise<RequestData>;
  updateRequestStatus: (id: string, status: RequestData['status'], options?: UpdateRequestStatusOptions) => Promise<void>;
  deleteRequest: (id: string) => Promise<void>;
  refreshRequests: () => Promise<void>;
  clearError: () => void;
  getRequestAnswers: (requestId: string) => Promise<Array<{ question: string; answer: string; questionId?: string; isRequestOnlyTrigger?: boolean; addedMinutes?: number; }>>;
  getRequestAttachments: (requestId: string) => Promise<Array<{ id: string; fileName: string; fileUrl: string; }>>;
}

interface SearchParams {
  status?: string;
  timeFilter?: string;
  searchQuery?: string;
  pageIndex?: number;
  pageSize?: number;
  dateSortField?: 'creationTime' | 'lastModificationTime'; // Which date field to sort by
}

const RequestsContext = createContext<RequestsContextType | undefined>(undefined);

export const useRequests = () => {
  const context = useContext(RequestsContext);
  if (context === undefined) {
    throw new Error('useRequests must be used within a RequestsProvider');
  }
  return context;
};

interface RequestsProviderProps {
  children: ReactNode;
}

/**
 * Parse a date string from the .NET backend as UTC.
 * The backend sends ISO timestamps like "2026-02-04T19:34:06.6263446" without a 'Z' suffix.
 * Without 'Z', JS Date() treats it as local time, causing incorrect relative time calculations.
 */
const parseApiDate = (dateStr: string | null | undefined): Date | null => {
  if (!dateStr) return null;
  let str = dateStr;
  if (str.includes('T') && !str.endsWith('Z') && !str.includes('+') && !str.includes('-', str.indexOf('T'))) {
    str = str + 'Z';
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

export const RequestsProvider: React.FC<RequestsProviderProps> = ({ children }) => {
  const [requests, setRequests] = useState<RequestData[]>([]);
  const [isLoading, setIsLoading] = useState(true); // Start with true for initial load
  const [error, setError] = useState<string | null>(null);
  const [lastSearchParams, setLastSearchParams] = useState<SearchParams>({});
  const { companyTimezone } = useTenantData();
  
  // Cache for full request details to avoid repeated API calls
  const fullDetailsCache = useRef<Map<string, RequestData>>(new Map());
  
  // Cache for booking form details to avoid repeated API calls
  const bookingFormCache = useRef<Map<string, { formName: string; allowInstantBooking?: boolean; leadSource?: string }>>(new Map());

  // Track requests currently being enriched to avoid duplicate fetches
  const enrichmentInProgress = useRef<Set<string>>(new Set());

  // Track booking form fetches in progress to avoid duplicate API calls
  const bookingFormFetchInProgress = useRef<Map<string, Promise<{ formName: string; allowInstantBooking?: boolean; leadSource?: string } | null>>>(new Map());

  // Transform full DTO to RequestData
  const transformDtoToRequest = (dto: Routemize_DTOs_RequestsDTO): RequestData => {
    // Extract answers manually to avoid hoisting issues
    const answers = (dto.answers || []).map(answer => ({
      question: answer.question || '',
      answer: answer.answer || '',
      questionId: answer.questionId || undefined
    }));
    
    // Extract attachments manually to avoid hoisting issues
    const attachments = (dto.attachments || []).map(attachment => ({
      id: attachment.id || '',
      fileName: attachment.fileName || '',
      fileUrl: attachment.filePath || '' // Use filePath from API as fileUrl
    }));
    
    // Extract form details
    // eventName is the form name (e.g., "Test", "Estimate")
    // eventType is NOT the form type - it's the appointment/estimate type (e.g., "On-Site Estimate")
    // The actual form type (Routemize vs Request) would be in bookingMethod, but it's not available in eventDetail
    const eventName = dto.eventDetail?.eventName || '';
    const estimateType = dto.eventDetail?.eventType || ''; // This is the estimate type, not form type
    
    // Parse eventName in case it contains "FormName - BookingMethod"
    let formName = eventName;
    let formType = undefined;
    
    if (eventName.includes(' - ')) {
      const parts = eventName.split(' - ');
      formName = parts[0]; // e.g., "Estimate"
      formType = parts[1]; // e.g., "Routemize"
    }
    
    const resolvedAddress = resolveContactAddressFromDto(dto.contact, dto.contactAddressId);
    const addressFields = contactAddressToFields(resolvedAddress);

    const transformed = {
      id: dto.id || '',
      date: parseApiDate(dto.creationTime) || new Date('2024-01-01'), // Fallback to fixed date, not current time
      status: mapStatus(dto.status),
      customerName: getCustomerName(dto.contact),
      customerEmail: dto.contact?.email || '',
      customerPhone: dto.contact?.phoneNumber || '',
      contactId: dto.contactId || dto.contact?.id || undefined,
      contactAddressId: dto.contactAddressId || undefined,
      businessName: dto.contact?.businessName || undefined,
      appointmentType: eventName || estimateType || '',
      formName: formName || undefined,
      formType: formType || undefined,
      bookingFormId: dto.bookingFormId || undefined,
      customerAddress: formatRequestAddressDisplay(resolvedAddress),
      // Individual address components — prefer the request's linked contact address
      address1: addressFields.address1,
      address2: addressFields.address2,
      city: addressFields.city,
      state: addressFields.state,
      zipCode: addressFields.zipCode,
      leadSource: dto.leadSource || dto.contact?.leadSource || undefined,
      answers: answers,
      attachments: attachments,
      assignedUserId: dto.assignedUserId || undefined,
      notes: dto.notes || '',
      projectDetail: dto.projectDetail || '',
      preferredDate: dto.preferredDate || '',
      preferredSecondaryDate: dto.preferredSecondaryDate || undefined,
      preferredTimeRange: dto.preferredTimeRange || undefined,
      preferredStartTime: dto.preferredStartTime || undefined,
      preferredEndTime: dto.preferredEndTime || undefined,
      autoRoutemized: dto.scheduleMethod === ('Automatic' as any) ? {
        date: dto.preferredDate || '',
        time: dto.preferredStartTime || ''
      } : undefined,
      summary: '',
      declinedDate: undefined,
      declinedBy: undefined,
      archivedDate: undefined,
      archivedBy: undefined,
      lastAction: undefined,
      isOutsideServiceArea: dto.rejectionReason === Routemize_Constants_SlotRejectionReason.OUTSIDE_SERVICE_AREA || !!(dto as any).isOutsideServiceArea,
      isAsap: dto.isAsap || false,
      isFlexibleDate: dto.isFlexibleDate || false,
      scheduleMethod: dto.scheduleMethod || undefined,
      origin: dto.origin || undefined,
      isLiveBooking: isLiveBookingStatus(dto.status, dto.origin),
      tags: dto.tags ?? [],
    };

    // Generate summary from complete data
    // TODO: Once backend supports aiSummary field in DTO, use it directly:
    // transformed.summary = (dto as any).aiSummary || RequestSummaryService.generateCardSummary({...})
    //
    // For now, use synchronous rule-based generation as fallback
    // When backend AI summary endpoint is ready, summaries will be generated server-side and cached
    transformed.summary = RequestSummaryService.generateCardSummary({
      appointmentType: transformed.appointmentType,
      answers: transformed.answers,
      projectDetail: transformed.projectDetail,
      notes: transformed.notes,
      preferredDate: transformed.preferredDate,
      preferredTime: dto.preferredTimeRange || undefined,
      customerName: transformed.customerName
    });
    
    // AI summary enhancement is disabled until backend endpoint is ready
    // The backend /api/app/ai/generateSummary endpoint currently returns 405
    // Uncomment when the endpoint is implemented:
    // enhanceSummaryWithAI(transformed);
    
    return transformed;
  };

  // Transform search response to RequestData (limited data)
  const transformSearchResponseToRequest = (searchResponse: Routemize_DTOs_RequestSearchResponse): RequestData => {
    const customerName = getCustomerNameFromSearch(searchResponse);
    
    // Parse the creation time properly
    const creationTime = parseApiDate(searchResponse.creationTime);
    
    // Use formName and allowInstantBooking from search response if available
    // These are now returned directly from the backend to avoid additional API calls
    const eventName = searchResponse.eventName || '';
    const formName = searchResponse.formName || eventName;
    const formType = searchResponse.allowInstantBooking === true ? 'Instant Booking' : searchResponse.allowInstantBooking === false ? 'Request' : undefined;
    
    // Extract lastAction info for declined and archived requests
    const lastAction = searchResponse.lastAction;
    const isDeclined = mapStatus(searchResponse.status) === 'Declined';
    const isArchived = mapStatus(searchResponse.status) === 'Archived';
    
    // Parse lastModificationTime from lastAction
    const lastModificationTime = parseApiDate(lastAction?.updatedDateTime) || undefined;
    
    const transformed = {
      id: searchResponse.id || '',
      date: creationTime || new Date('2024-01-01'), // Use a fallback date instead of current time
      lastModificationTime: lastModificationTime, // Store last modification time
      status: mapStatus(searchResponse.status),
      customerName,
      customerEmail: '', // Not available in search response
      customerPhone: '', // Not available in search response
      contactId: undefined, // Not available in search response - will be loaded with full details
      contactAddressId: undefined, // Not available in search response - will be loaded with full details
      businessName: undefined, // Not available in search response - will be loaded with full details
      appointmentType: eventName,
      formName: formName || undefined,
      formType: formType || undefined,
      bookingFormId: searchResponse.bookingFormId || undefined, // Now available in search response
      customerAddress: formatAddressFromSearch(searchResponse), // Now available in search response
      // Individual address components from search response
      address1: searchResponse.addressLine1 || undefined,
      address2: searchResponse.addressLine2 || undefined,
      city: searchResponse.city || undefined,
      state: searchResponse.state || undefined,
      zipCode: searchResponse.zipCode || undefined,
      leadSource: searchResponse.leadSource || undefined, // Now available in search response
      answers: [], // Not available in search response
      attachments: [], // Not available in search response
      assignedUserId: undefined, // Not available in search response
      notes: searchResponse.notes || '',
      projectDetail: searchResponse.projectDetail || '',
      preferredDate: '', // Not available in search response
      preferredSecondaryDate: undefined, // Not available in search response
      preferredTimeRange: undefined, // Not available in search response
      preferredStartTime: undefined, // Not available in search response
      preferredEndTime: undefined, // Not available in search response
      autoRoutemized: searchResponse.scheduleMethod === ('Automatic' as any) && creationTime ? {
        date: creationTime.toLocaleDateString('en-US', companyTimezone ? { timeZone: companyTimezone } : undefined),
        time: creationTime.toLocaleTimeString('en-US', companyTimezone ? { timeZone: companyTimezone } : undefined)
      } : undefined,
      summary: '', // Will be generated on-demand or when full details are loaded from backend cache
      declinedDate: isDeclined ? parseApiDate(lastAction?.updatedDateTime) || undefined : undefined,
      declinedBy: isDeclined ? lastAction?.updatedBy || undefined : undefined,
      archivedDate: isArchived ? parseApiDate(lastAction?.updatedDateTime) || undefined : undefined,
      archivedBy: isArchived ? lastAction?.updatedBy || undefined : undefined,
      lastAction: lastAction ? {
        updatedDateTime: lastAction.updatedDateTime || undefined,
        updatedBy: lastAction.updatedBy || undefined,
        updatedAction: lastAction.updatedAction || undefined
      } : undefined,
      isOutsideServiceArea: searchResponse.rejectionReason === Routemize_Constants_SlotRejectionReason.OUTSIDE_SERVICE_AREA || !!(searchResponse as any).isOutsideServiceArea,
      isAsap: searchResponse.isAsap || false,
      isFlexibleDate: false,
      scheduleMethod: searchResponse.scheduleMethod || undefined,
      origin: searchResponse.origin || undefined,
      isLiveBooking: isLiveBookingStatus(searchResponse.status, searchResponse.origin),
      tags: searchResponse.tags ?? [],
      noteCount: searchResponse.noteCount ?? 0,
      latestInternalNotePreview: searchResponse.latestInternalNotePreview || undefined,
    };

    // Generate summary from available data
    // TODO: Once backend supports aiSummary field in search response, use it directly
    // For now, use synchronous rule-based generation
    transformed.summary = RequestSummaryService.generateCardSummary({
      appointmentType: transformed.appointmentType,
      answers: transformed.answers,
      projectDetail: transformed.projectDetail,
      notes: transformed.notes,
      preferredDate: transformed.preferredDate,
      customerName: transformed.customerName
    });
    
    return transformed;
  };

  /**
   * Asynchronously enhance request summary with AI-generated content
   * Falls back silently if backend AI service is not available
   * Updates the request in state once AI summary is generated
   */
  const enhanceSummaryWithAI = async (request: RequestData): Promise<void> => {
    // Skip if no meaningful data to summarize
    if (!request.appointmentType && !request.notes && !request.projectDetail && request.answers.length === 0) {
      return;
    }

    try {
      // Attempt to generate AI summary from backend
      const aiSummary = await AISummaryService.generateRequestSummary(
        {
          appointmentType: request.appointmentType,
          customerName: request.customerName,
          answers: request.answers,
          projectDetail: request.projectDetail,
          notes: request.notes,
          preferredDate: request.preferredDate,
          preferredTime: request.preferredTimeRange,
        },
        request.id // Pass ID for backend caching
      );

      // Update request with AI-generated summary
      setRequests(prevRequests => 
        prevRequests.map(r => 
          r.id === request.id ? { ...r, summary: aiSummary } : r
        )
      );

      // Also update cache if this request is cached
      if (fullDetailsCache.current.has(request.id)) {
        const cachedRequest = fullDetailsCache.current.get(request.id);
        if (cachedRequest) {
          fullDetailsCache.current.set(request.id, { ...cachedRequest, summary: aiSummary });
        }
      }
    } catch (error) {
      // Silently fail - the rule-based summary is already in place
      // Backend may not be available yet or AI service may be down
    }
  };

  const getCustomerNameFromSearch = (searchResponse: Routemize_DTOs_RequestSearchResponse): string => {
    const firstName = searchResponse.contactFirstName || '';
    const lastName = searchResponse.contactLastName || '';
    return `${firstName} ${lastName}`.trim() || 'Unknown Customer';
  };

  const getCustomerName = (contact?: any): string => {
    if (!contact) return '';
    
    // Try fullName first, then construct from first/last name, then use displayName
    if (contact.fullName) return contact.fullName;
    if (contact.firstName || contact.lastName) {
      return `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
    }
    if (contact.displayName) return contact.displayName;
    if (contact.businessName) return contact.businessName;
    
    return '';
  };

  // Granular booking-flow statuses that mean the customer is actively on the form right now.
  // Excludes plain Scheduling which is the default initial state for all incomplete requests.
  const LIVE_BOOKING_STATUSES: Set<string> = new Set([
    Routemize_Constants_RequestStatus.CONTACT_PROVIDED,
    Routemize_Constants_RequestStatus.ADDRESS_PROVIDED,
    Routemize_Constants_RequestStatus.SERVICE_DETAILS_COMPLETED,
    Routemize_Constants_RequestStatus.SLOTS_PRESENTED,
    Routemize_Constants_RequestStatus.PREVIEW_SHOWN,
  ]);

  // Live booking should only ever reflect a customer actively progressing through
  // a public-facing channel (public booking form, chatbot, or voice agent).
  // Internal/operator-originated requests must never be flagged as live, since an
  // operator running the Routemize flow advances the same granular statuses.
  const CUSTOMER_LIVE_BOOKING_ORIGINS: Set<string> = new Set([
    Routemize_Constants_RequestOrigin.WEB,
    Routemize_Constants_RequestOrigin.CHATBOT,
    Routemize_Constants_RequestOrigin.VOICE_BOT,
  ]);

  const isLiveBookingStatus = (
    status?: Routemize_Constants_RequestStatus,
    origin?: Routemize_Constants_RequestOrigin,
  ): boolean => {
    if (!status || !LIVE_BOOKING_STATUSES.has(status)) return false;
    return !!origin && CUSTOMER_LIVE_BOOKING_ORIGINS.has(origin);
  };

  const mapStatus = (status?: Routemize_Constants_RequestStatus): RequestData['status'] => {
    switch(status) {
      case 'Scheduling': return 'Incomplete';
      case 'Active': return 'Active';
      case 'Archived': return 'Archived';
      case 'History': return 'History';
      case 'Declined': return 'Declined';
      case Routemize_Constants_RequestStatus.CONTACT_PROVIDED:
      case Routemize_Constants_RequestStatus.ADDRESS_PROVIDED:
      case Routemize_Constants_RequestStatus.SERVICE_DETAILS_COMPLETED:
      case Routemize_Constants_RequestStatus.SLOTS_PRESENTED:
      case Routemize_Constants_RequestStatus.PREVIEW_SHOWN:
        return 'Incomplete';
      case Routemize_Constants_RequestStatus.CONFIRMED:
        return 'Active';
      default: return 'Active';
    }
  };

  const mapStatusToEnum = (status: RequestData['status']): Routemize_Constants_RequestStatus => {
    switch(status) {
      case 'Incomplete': return Routemize_Constants_RequestStatus.SCHEDULING;
      case 'Active': return Routemize_Constants_RequestStatus.ACTIVE;
      case 'Archived': return Routemize_Constants_RequestStatus.ARCHIVED;
      case 'History': return Routemize_Constants_RequestStatus.HISTORY;
      case 'Declined': return Routemize_Constants_RequestStatus.DECLINED;
      default: return Routemize_Constants_RequestStatus.ACTIVE;
    }
  };

  const mapDateFilter = (timeFilter: string): Routemize_Constants_DateFilterType => {
    switch(timeFilter) {
      case 'Last 24 hours': return Routemize_Constants_DateFilterType.LAST24HOURS;
      case 'Last 7 Days': return Routemize_Constants_DateFilterType.LAST7DAYS;
      case 'Last 30 days': return Routemize_Constants_DateFilterType.LAST30DAYS;
      case 'Custom Range': return Routemize_Constants_DateFilterType.NONE; // Custom range not yet supported by backend
      // Legacy filters for backward compatibility
      case '24 Hours': return Routemize_Constants_DateFilterType.LAST24HOURS;
      case 'This week': return Routemize_Constants_DateFilterType.THIS_WEEK;
      case 'This Month': return Routemize_Constants_DateFilterType.THIS_MONTH;
      case 'This Year': return Routemize_Constants_DateFilterType.THIS_YEAR;
      default: return Routemize_Constants_DateFilterType.NONE;
    }
  };
  
  // Client-side date filtering for rolling day ranges
  // Backend only supports calendar periods (this week, this month), not rolling days
  const applyClientSideDateFilter = (requests: RequestData[], timeFilter: string, dateSortField: 'creationTime' | 'lastModificationTime'): RequestData[] => {
    if (timeFilter !== 'Last 7 Days' && timeFilter !== 'Last 30 days') {
      return requests; // No client-side filtering needed
    }
    
    const now = new Date();
    const daysToSubtract = timeFilter === 'Last 7 Days' ? 7 : 30;
    const cutoffDate = new Date(now);
    cutoffDate.setDate(cutoffDate.getDate() - daysToSubtract);
    
    const filtered = requests.filter(request => {
      // Use the appropriate date field based on sort preference
      const dateToCheck = dateSortField === 'lastModificationTime' 
        ? request.lastModificationTime || request.date 
        : request.date;
      
      return dateToCheck >= cutoffDate;
    });
    
    return filtered;
  };

  const formatAddress = (contact?: Routemize_DTOs_RequestsDTO['contact']): string => {
    const resolvedAddress = resolveContactAddressFromDto(contact);
    return formatRequestAddressDisplay(resolvedAddress);
  };

  const applyResolvedAddressFields = async (
    request: RequestData,
    contact?: Routemize_DTOs_RequestsDTO['contact'],
    contactAddressId?: string | null
  ): Promise<RequestData> => {
    const resolvedFields = await resolveRequestAddressFields(contact, contactAddressId);
    const mergedFields = mergeRequestAddressFields(
      {
        address1: request.address1,
        address2: request.address2,
        city: request.city,
        state: request.state,
        zipCode: request.zipCode,
      },
      resolvedFields
    );

    return {
      ...request,
      ...mergedFields,
      customerAddress: formatRequestAddressDisplay(mergedFields) || request.customerAddress,
    };
  };

  const formatAddressFromSearch = (searchResponse: Routemize_DTOs_RequestSearchResponse): string => {
    const parts = [
      searchResponse.addressLine1 || '',
      searchResponse.city || '',
      searchResponse.state || '',
      searchResponse.zipCode || ''
    ].filter(part => part.trim() !== '');

    return parts.join(', ');
  };

  const transformAnswers = (answers: Routemize_DTOs_RequestAnswerDTO[]): Array<{ question: string; answer: string; questionId?: string; isRequestOnlyTrigger?: boolean; addedMinutes?: number; }> => {
    return answers.map(answer => ({
      question: answer.question || '',
      answer: answer.answer || '',
      questionId: answer.questionId || undefined
    }));
  };

  const transformAttachments = (attachments: Routemize_DTOs_AttachmentsDTO[]): Array<{ id: string; fileName: string; fileUrl: string; }> => {
    return attachments.map(attachment => ({
      id: attachment.id || '',
      fileName: attachment.fileName || '',
      fileUrl: attachment.filePath || '' // Use filePath from API as fileUrl
    }));
  };

  // Enrich requests with address and lead source data by fetching full details
  // This runs in the background after initial load to avoid blocking the UI
  const enrichRequestsWithAddresses = async (requestsToEnrich: RequestData[]) => {
    const requestsNeedingEnrichment = requestsToEnrich.filter(r => !r.customerAddress || !r.leadSource);
    
    if (requestsNeedingEnrichment.length === 0) return;
    
    // Batch the enrichment to avoid too many concurrent requests
    const batchSize = 5;
    const batches: RequestData[][] = [];
    for (let i = 0; i < requestsNeedingEnrichment.length; i += batchSize) {
      batches.push(requestsNeedingEnrichment.slice(i, i + batchSize));
    }
    
    for (const batch of batches) {
      const enrichmentPromises = batch.map(async (request) => {
        // Skip if already enriching this request
        if (enrichmentInProgress.current.has(request.id)) return null;
        
        // Check cache first
        const cached = fullDetailsCache.current.get(request.id);
        if (cached?.customerAddress && cached?.leadSource) {
          return { id: request.id, address: cached.customerAddress, leadSource: cached.leadSource };
        }
        
        enrichmentInProgress.current.add(request.id);
        
        try {
          const dto = await RequestsService.getApiAppRequestsGetById(request.id);
          let fullRequest = transformDtoToRequest(dto);
          fullRequest = await applyResolvedAddressFields(fullRequest, dto.contact, dto.contactAddressId);
          const address = fullRequest.customerAddress || formatAddress(dto.contact);
          
          // Also fetch booking form details for the form-level lead source
          let formLeadSource: string | undefined;
          if (dto.bookingFormId) {
            const formDetails = await getBookingFormDetails(dto.bookingFormId);
            if (formDetails) {
              formLeadSource = formDetails.leadSource;
              fullRequest.leadSource = formDetails.leadSource || fullRequest.leadSource;
            }
          }
          
          fullDetailsCache.current.set(request.id, fullRequest);
          
          return { 
            id: request.id, 
            address, 
            leadSource: formLeadSource || fullRequest.leadSource 
          };
        } catch (err) {
          console.error(`Error enriching request ${request.id} with address:`, err);
          return null;
        } finally {
          enrichmentInProgress.current.delete(request.id);
        }
      });
      
      const results = await Promise.all(enrichmentPromises);
      
      // Update requests with the enriched data
      const updates = results.filter((r): r is { id: string; address: string; leadSource?: string } => r !== null);
      
      if (updates.length > 0) {
        setRequests(prev => prev.map(req => {
          const update = updates.find(u => u.id === req.id);
          if (update) {
            return { 
              ...req, 
              customerAddress: update.address || req.customerAddress,
              leadSource: update.leadSource || req.leadSource
            };
          }
          return req;
        }));
      }
    }
  };

  // Fetch booking form details to get formName, allowInstantBooking, and leadSource
  const getBookingFormDetails = async (bookingFormId: string): Promise<{ formName: string; allowInstantBooking?: boolean; leadSource?: string } | null> => {
    // Check cache first
    const cached = bookingFormCache.current.get(bookingFormId);
    if (cached) {
      return cached;
    }

    // Check if a fetch is already in progress for this booking form
    const inProgress = bookingFormFetchInProgress.current.get(bookingFormId);
    if (inProgress) {
      // Wait for the existing fetch to complete
      return inProgress;
    }

    // Create the fetch promise and track it
    const fetchPromise = (async () => {
      try {
        const formDetails = await BookingFormService.getApiAppBookingFormGetById(bookingFormId);
        const result = {
          formName: formDetails.formName || '',
          allowInstantBooking: formDetails.allowInstantBooking ?? undefined,
          leadSource: formDetails.leadSource || undefined
        };

        // Cache the result
        bookingFormCache.current.set(bookingFormId, result);

        return result;
      } catch (err) {
        console.error('Error fetching booking form details:', err);
        return null;
      } finally {
        // Remove from in-progress tracking
        bookingFormFetchInProgress.current.delete(bookingFormId);
      }
    })();

    // Track the in-progress fetch
    bookingFormFetchInProgress.current.set(bookingFormId, fetchPromise);

    return fetchPromise;
  };

  // Lazy load full request details for incomplete search results
  const enrichRequestWithFullDetails = async (requestId: string): Promise<RequestData | null> => {
    try {
      // Check cache first
      const cached = fullDetailsCache.current.get(requestId);
      if (cached) {
        return cached;
      }
      
      // Check if enrichment is already in progress for this request
      if (enrichmentInProgress.current.has(requestId)) {
        // Wait a bit and check cache again (another call is fetching it)
        await new Promise(resolve => setTimeout(resolve, 100));
        const nowCached = fullDetailsCache.current.get(requestId);
        if (nowCached) return nowCached;
      }
      
      // Mark as in progress
      enrichmentInProgress.current.add(requestId);
      
      // Fetch full details from API
      const fullDto = await RequestsService.getApiAppRequestsGetById(requestId);
      let fullData = transformDtoToRequest(fullDto);
      fullData = await applyResolvedAddressFields(fullData, fullDto.contact, fullDto.contactAddressId);
      
      // Enrich with booking form details if available
      if (fullDto.bookingFormId) {
        const formDetails = await getBookingFormDetails(fullDto.bookingFormId);
        if (formDetails) {
          fullData.formName = formDetails.formName || fullData.formName;
          fullData.formType = formDetails.allowInstantBooking === true ? 'Instant Booking' : formDetails.allowInstantBooking === false ? 'Request' : fullData.formType;
          // Use lead source from form, not from contact
          fullData.leadSource = formDetails.leadSource || fullData.leadSource;
        }
      }
      
      // Cache the result
      fullDetailsCache.current.set(requestId, fullData);
      
      // Remove from in-progress set
      enrichmentInProgress.current.delete(requestId);
      
      return fullData;
    } catch (err) {
      console.error('Error enriching request with full details:', err);
      // Remove from in-progress set on error too
      enrichmentInProgress.current.delete(requestId);
      return null;
    }
  };

  const searchRequests = async (params: SearchParams = {}) => {
    setIsLoading(true);
    setError(null);
    setLastSearchParams(params);
    
    try {
      // Determine sorting field based on dateSortField parameter.
      // The search endpoint projects to RequestSearchResponse, which exposes the
      // last-modified timestamp nested as LastAction.UpdatedDateTime (there is no
      // top-level LastModificationTime) — sort by that path, not the entity column.
      const sortField = params.dateSortField === 'lastModificationTime' ? 'LastAction.UpdatedDateTime' : 'CreationTime';
      
      const searchFilter: Routemize_DTOs_SearchParameters_PagedSortedAndFilteredRequestDto_1 = {
        skipCount: (params.pageIndex || 0) * (params.pageSize || 10),
        maxResultCount: params.pageSize || 10,
        sorting: `${sortField} desc`,
        filter: {
          status: params.status ? mapStatusToEnum(params.status as RequestData['status']) : undefined,
          dateFilter: params.timeFilter ? mapDateFilter(params.timeFilter) : undefined,
          searchText: params.searchQuery && params.searchQuery.length >= 2 ? params.searchQuery : undefined
        }
      };

      const response = await RequestsService.postApiAppRequestsSearch(searchFilter);
      
      // Cast the response to the correct type since the generated type is incorrect
      const typedResponse = response as any;
      const searchResponses = (typedResponse.items || []) as Routemize_DTOs_RequestSearchResponse[];
      
      // Use the search response transformation function
      let transformedRequests = searchResponses.map(searchResponse => transformSearchResponseToRequest(searchResponse));
      
      // Apply client-side date filtering for rolling day ranges (Last 7 Days, Last 30 days)
      // Backend only supports calendar periods, so we need to filter client-side for true rolling ranges
      transformedRequests = applyClientSideDateFilter(transformedRequests, params.timeFilter || '', params.dateSortField || 'creationTime');
      
      setRequests(transformedRequests);

      // Stop loading - main data is ready
      setIsLoading(false);

      // NOTE: Address enrichment removed to fix N+1 API calls issue
      // Addresses will be loaded on-demand when viewing request details
      // If addresses are needed in list view, add them to the backend search response DTO
    } catch (err: any) {
      console.error('Error searching requests:', err.status, err.message);
      
      // Handle different error types
      let errorMessage = 'Failed to load requests';
      
      if (err.status === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (err.status === 403) {
        // Role-aware message when the backend supplied role context; generic otherwise.
        errorMessage = resolveForbiddenMessage(err) ?? 'You do not have permission to access requests.';
      } else if (err.status === 404) {
        errorMessage = 'Requests API endpoint not found.';
      } else if (err.status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else if (err.body?.error?.message) {
        errorMessage = err.body.error.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setIsLoading(false);
      // Keep existing requests on error instead of clearing them
    }
  };

  const getRequestById = async (id: string): Promise<RequestData> => {
    try {
      const dto = await RequestsService.getApiAppRequestsGetById(id);
      let request = transformDtoToRequest(dto);
      request = await applyResolvedAddressFields(request, dto.contact, dto.contactAddressId);
      return request;
    } catch (err: any) {
      console.error('Error getting request by ID:', err);
      const errorMessage = err.body?.error?.message || err.message || 'Failed to load request details';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const updateRequestStatus = async (id: string, status: RequestData['status'], options?: UpdateRequestStatusOptions) => {
    try {
      // Optimistic update - immediately update local state
      setRequests(prev => {
        const updated = prev.map(req => 
          req.id === id 
            ? { 
                ...req, 
                status,
                // Clear declined fields when reopening (status changes away from Declined)
                declinedDate: status === 'Declined' ? new Date() : undefined,
                declinedBy: status === 'Declined' ? req.declinedBy : undefined,
                // Clear archived fields when reopening (status changes away from Archived)
                archivedDate: status === 'Archived' ? (req.archivedDate || new Date()) : undefined,
                archivedBy: status === 'Archived' ? req.archivedBy : undefined
              } 
            : req
        );
        return updated;
      });

      // Then make API call
      const updateDto: Routemize_DTOs_UpdateRequestsDTO = {
        id,
        status: mapStatusToEnum(status),
        suppressNotifications: options?.suppressNotifications,
        // The server writes these as a StatusReason note on the request (NC-04, D9).
        statusReason: options?.statusReason,
        statusReasonDetails: options?.statusReasonDetails
      };
      const dto = await RequestsService.putApiAppRequestsUpdate(updateDto);
      
      // Update with server response, but preserve existing data that may not be returned
      const serverData = transformDtoToRequest(dto);
      setRequests(prev => {
        const updated = prev.map(req => {
          if (req.id !== id) return req;
          
          // Merge: keep existing data, overlay server response, then apply status-specific fields
          return {
            ...req, // Keep all existing data (customerName, customerEmail, etc.)
            ...serverData, // Overlay with server response
            // Preserve fields that might be missing from server response
            customerName: serverData.customerName || req.customerName,
            customerEmail: serverData.customerEmail || req.customerEmail,
            customerPhone: serverData.customerPhone || req.customerPhone,
            customerAddress: serverData.customerAddress || req.customerAddress,
            address1: serverData.address1 || req.address1,
            address2: serverData.address2 || req.address2,
            city: serverData.city || req.city,
            state: serverData.state || req.state,
            zipCode: serverData.zipCode || req.zipCode,
            contactAddressId: serverData.contactAddressId || req.contactAddressId,
            formName: serverData.formName || req.formName,
            formType: serverData.formType || req.formType,
            leadSource: serverData.leadSource || req.leadSource,
            // The full-details DTO has no note summary; keep the search's (NC-08)
            noteCount: req.noteCount,
            latestInternalNotePreview: req.latestInternalNotePreview,
            // Clear status-specific fields when not in that status
            declinedDate: status === 'Declined' ? (serverData.declinedDate || new Date()) : undefined,
            declinedBy: status === 'Declined' ? serverData.declinedBy : undefined,
            archivedDate: status === 'Archived' ? serverData.archivedDate : undefined,
            archivedBy: status === 'Archived' ? serverData.archivedBy : undefined
          };
        });
        return updated;
      });
      
      // Clear cache to ensure fresh data on next load
      fullDetailsCache.current.delete(id);
    } catch (err: any) {
      console.error('Error updating request status:', err);
      const errorMessage = err.body?.error?.message || err.message || 'Failed to update request status';
      setError(errorMessage);
      
      // Revert optimistic update on error - reload requests
      await refreshRequests();
      throw new Error(errorMessage);
    }
  };

  const deleteRequest = async (id: string) => {
    try {
      await RequestsService.deleteApiAppRequestsDelete(id);
      setRequests(prev => prev.filter(req => req.id !== id));
    } catch (err: any) {
      console.error('Error deleting request:', err);
      const errorMessage = err.body?.error?.message || err.message || 'Failed to delete request';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const getRequestAnswers = async (requestId: string): Promise<Array<{ question: string; answer: string; questionId?: string; isRequestOnlyTrigger?: boolean; addedMinutes?: number; }>> => {
    try {
      const answers = await RequestsService.getApiAppRequestsGetAnswers(requestId);
      return transformAnswers(answers);
    } catch (err: any) {
      console.error('Error getting request answers:', err);
      const errorMessage = err.body?.error?.message || err.message || 'Failed to load request answers';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const getRequestAttachments = async (requestId: string): Promise<Array<{ id: string; fileName: string; fileUrl: string; }>> => {
    try {
      const attachments = await RequestsService.getApiAppRequestsGetAttachements(requestId);
      return transformAttachments(attachments);
    } catch (err: any) {
      console.error('Error getting request attachments:', err);
      const errorMessage = err.body?.error?.message || err.message || 'Failed to load request attachments';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const refreshRequests = async () => {
    await searchRequests(lastSearchParams);
  };

  const clearError = () => {
    setError(null);
  };

  const value = {
    requests,
    isLoading,
    error,
    searchRequests,
    getRequestById,
    updateRequestStatus,
    deleteRequest,
    refreshRequests,
    clearError,
    getRequestAnswers,
    getRequestAttachments
  };

  return (
    <RequestsContext.Provider value={value}>
      {children}
    </RequestsContext.Provider>
  );
};